import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getMe } from '@/features/auths/api/get-me.api';
import { login } from '@/features/auths/api/log-in.api';
import { logout } from '@/features/auths/api/log-out.api';
import type { LoginPayload } from '@/features/auths/schemas/log-in.schema';
import { authLocalStorage } from '@/features/auths/utils/auth-local-storage';

import { type AuthContext, authContext } from './auth-context';
import { APP_ROUTE } from '@/app/app.route.constant';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

/** Stable query key used to cache and invalidate the current-user (get-me) query. */
const GET_ME_QUERY_KEY = 'auth-me';

/**
 * Provides authentication state and actions to the entire app via React context.
 *
 * Responsibilities:
 * - Manages session state based on the presence of an access token in localStorage.
 * - Fetches the current user (get-me) when a session exists.
 * - Exposes `signIn` / `signOut` mutations that handle token persistence and cache invalidation.
 *
 * Must be rendered inside a `QueryClientProvider` (TanStack Query).
 */
export default function AuthProvider({ children }: React.PropsWithChildren) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /** Shared TanStack Query client for invalidating and removing auth-related cache. */
  const queryClient = useQueryClient();

  /**
   * Session flag — true when an access token is present in localStorage.
   * Initialized eagerly on mount so the get-me query fires immediately for returning users.
   */
  const [hasSession, setHasSession] = useState(() =>
    Boolean(authLocalStorage.getAccessToken())
  );

  /**
   * Fetches the current admin profile when a session is active.
   * Disabled when `hasSession` is false to avoid unnecessary 401 requests.
   */
  const getMeQuery = useQuery({
    queryKey: [GET_ME_QUERY_KEY],
    queryFn: getMe,
    enabled: hasSession,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  /** Resolved user from get-me, or null when logged out / not yet resolved. */
  const user = getMeQuery.data ?? null;
  /** True when a token exists but the get-me request hasn't resolved yet. */
  const isLoadingAuth = hasSession && getMeQuery.isPending;
  /** Stable reference to the get-me refetch function. */
  const refetchGetMe = getMeQuery.refetch;

  /**
   * Cleanup handler invoked when sign-out settles (success or failure).
   * Clears persisted tokens, resets the session flag, and removes cached user data.
   */
  function handleSignOutSettled() {
    authLocalStorage.clearAuthTokens();
    setHasSession(false);
    queryClient.removeQueries({ queryKey: [GET_ME_QUERY_KEY] });
  }

  const signOutMutation = useMutation({
    mutationFn: logout,
    onSettled: handleSignOutSettled,
  });

  /**
   * Sign-in mutation.
   * On success, persists both tokens and triggers a fresh get-me fetch
   * so the UI immediately reflects the authenticated state.
   */
  const signInMutation = useMutation({
    mutationFn: login,
    onError: _error => {
      toast.error('Terjadi Kesalahan Pada Saat Login!');
    },
    onSuccess: data => {
      const user = data?.user || null;
      authLocalStorage.setAccessToken(data.accessToken);
      authLocalStorage.setRefreshToken(data.refreshToken);
      authLocalStorage.setUser(user);
      setHasSession(true);
      queryClient.invalidateQueries({ queryKey: [GET_ME_QUERY_KEY] });
      toast.success('Success Login sebagai Admin!');

      const redirectTo = searchParams.get('redirectTo');
      navigate(redirectTo || APP_ROUTE.ANALYTICS.DASHBOARD.getHref(), {
        replace: true,
      });
    },
  });

  /** Stable sign-in callback exposed via context. */
  const signIn = useCallback(
    async (params: LoginPayload) => {
      await signInMutation.mutateAsync(params);
    },
    [signInMutation]
  );

  /** Stable sign-out callback exposed via context. */
  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync();
  }, [signOutMutation]);

  /**
   * Memoized context value derived from query/mutation state and stable callbacks.
   * Only re-creates when one of the tracked dependencies changes.
   */
  const value = useMemo(
    (): AuthContext => ({
      user,
      isLoadingAuth,
      isAuthenticated: user !== null && hasSession && !isLoadingAuth,
      isSigningIn: signInMutation.isPending,
      isSigningOut: signOutMutation.isPending,
      isFetchUser: getMeQuery.isFetching,
      signIn,
      signOut,
      refetchUser: refetchGetMe,
    }),
    [
      user,
      hasSession,
      isLoadingAuth,
      signInMutation.isPending,
      signOutMutation.isPending,
      getMeQuery.isFetching,
      signIn,
      signOut,
      refetchGetMe,
    ]
  );

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
}
